/**
 * Stops iOS WKWebView from scrolling the document (the "outer window")
 * around the in-app lists. That rubber-band shoves the whole UI into the
 * status bar and leaves a gap under the nav.
 *
 * Nested overflow:auto/scroll lists keep working. Login / legal pages have
 * no `.main-layout` and still scroll the document.
 */

const SCROLLABLE_OVERFLOW = new Set(['auto', 'scroll', 'overlay']);
const APP_SHELL_SELECTOR = '.main-layout';

export function isAppShellMounted(): boolean {
	return document.querySelector(APP_SHELL_SELECTOR) !== null;
}

/**
 * `deltaY > 0` is a downward finger move (content toward the top);
 * `deltaY < 0` is an upward finger move (content toward the bottom).
 */
export function canScrollVertically(el: Element, deltaY: number): boolean {
	if (!(el instanceof HTMLElement)) return false;
	if (el.scrollHeight <= el.clientHeight) return false;
	const atTop = el.scrollTop <= 0;
	const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
	if (deltaY > 0 && !atTop) return true;
	if (deltaY < 0 && !atBottom) return true;
	return false;
}

export function hasNestedVerticalScroller(target: EventTarget | null, deltaY: number): boolean {
	let el = target instanceof Element ? target : null;
	while (el && el !== document.body && el !== document.documentElement) {
		const overflowY = getComputedStyle(el).overflowY;
		if (SCROLLABLE_OVERFLOW.has(overflowY) && canScrollVertically(el, deltaY)) {
			return true;
		}
		el = el.parentElement;
	}

	// Signed-in shell: never treat the document as a scroller. Extra html/body
	// height (or WKWebView bounce) is what pans the whole UI.
	if (isAppShellMounted()) return false;

	const scrollingElement = document.scrollingElement ?? document.documentElement;
	return scrollingElement ? canScrollVertically(scrollingElement, deltaY) : false;
}

export function pinDocumentScrollToTop(): void {
	if (!isAppShellMounted()) return;
	const scrollTop =
		window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
	if (scrollTop !== 0) {
		window.scrollTo(0, 0);
	}
}

/**
 * Always-on iOS touch lock. CSS overscroll-behavior is not enough in WKWebView;
 * canceling unhandled `touchmove` is what actually stops the outer window.
 */
export function attachIosDocumentScrollLock(): () => void {
	let touchStartY = 0;
	let touchStartX = 0;

	const onTouchStart = (event: TouchEvent) => {
		touchStartY = event.touches[0]?.clientY ?? 0;
		touchStartX = event.touches[0]?.clientX ?? 0;
	};

	const onTouchMove = (event: TouchEvent) => {
		if (event.touches.length > 1) return;

		const touch = event.touches[0];
		if (!touch) return;

		const deltaY = touch.clientY - touchStartY;
		const deltaX = touch.clientX - touchStartX;
		if (Math.abs(deltaY) <= Math.abs(deltaX)) return;

		if (!hasNestedVerticalScroller(event.target, deltaY) && event.cancelable) {
			event.preventDefault();
		}
	};

	const onWindowScroll = () => {
		pinDocumentScrollToTop();
	};

	document.addEventListener('touchstart', onTouchStart, { passive: true });
	document.addEventListener('touchmove', onTouchMove, { passive: false });
	window.addEventListener('scroll', onWindowScroll, { passive: true });
	pinDocumentScrollToTop();

	return () => {
		document.removeEventListener('touchstart', onTouchStart);
		document.removeEventListener('touchmove', onTouchMove);
		window.removeEventListener('scroll', onWindowScroll);
	};
}
